"use client";

import type { BotIdTags } from "@homeinstead/analytics";
import type {
  DefaultValues,
  FieldValues,
  Resolver,
  UseFormReturn,
} from "react-hook-form";
import type { ZodType } from "zod";
import type { LeadResponse } from "~www/types/forms/lead-response";

import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { useActionState, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHandleFormSubmit } from "~www/hooks/useHandleFormSubmit";
import { getInquirySubmitErrorMessage } from "./get-inquiry-submit-error-message";
import { useInquirySubmissionTracking } from "./use-inquiry-submission-tracking";

type InquirySubmitResult = "success" | "server-error" | "validation-error";

type InquirySubmitAction = (
  prevState: LeadResponse<unknown> | null,
  formData: FormData,
) => Promise<LeadResponse<unknown>>;

type UseInquiryLeadFormOptions<TForm extends FieldValues> = {
  eventName: string;
  getDefaultValues: () => TForm;
  getTrackingProperties: (
    result: InquirySubmitResult,
    getValues: UseFormReturn<TForm>["getValues"],
    tags: BotIdTags,
  ) => Record<string, unknown>;
  schema: ZodType<TForm>;
  submitAction: InquirySubmitAction;
  thankYouPath: string;
};

function useInquiryLeadForm<TForm extends FieldValues>({
  eventName,
  getDefaultValues,
  getTrackingProperties,
  schema,
  submitAction,
  thankYouPath,
}: UseInquiryLeadFormOptions<TForm>) {
  const router = useRouter();
  const [step, setStep] = useState<1 | 2>(1);

  const defaultValues = useMemo(() => getDefaultValues(), [getDefaultValues]);

  const methods = useForm<TForm, unknown, TForm>({
    defaultValues: defaultValues as DefaultValues<TForm>,
    mode: "onSubmit",
    resolver: zodResolver(schema) as Resolver<TForm>,
    shouldUnregister: false,
  });

  const { getValues } = methods;

  const [state, submitFormAction, isPending] = useActionState(
    submitAction,
    null,
  ) as [LeadResponse<unknown> | null, typeof submitFormAction, boolean];

  const handleFormSubmit = useHandleFormSubmit<TForm>(methods);

  useInquirySubmissionTracking(state, {
    eventName,
    getProperties: getTrackingProperties,
    getValues,
  });

  const isRedirecting = state?.ok === true;
  const submitError =
    state?.ok === false && !state.validationErrors
      ? getInquirySubmitErrorMessage(state.error)
      : undefined;

  useEffect(() => {
    if (!isRedirecting) {
      return;
    }
    router.replace(thankYouPath);
  }, [isRedirecting, router, thankYouPath]);

  return {
    handleFormSubmit,
    isPending,
    isRedirecting,
    methods,
    setStep,
    step,
    submitError,
    submitFormAction,
  };
}

export { useInquiryLeadForm };
